import { useEffect, useState } from "react";
import { CalendarDays, Loader2, User } from "lucide-react";
import { CoordinatorLayout } from "@/components/layout/coordinator-layout";
import { Button } from "@/components/ui/button";
import { SameTeamBadge } from "@/components/ui/same-team-badge";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface AppointmentRow {
  id: number;
  patientName: string | null;
  status: string | null;
  category: string | null;
  preferredDate: string | null;
  createdAt: string;
  detail: string | null;
  assignedToUserId: string | null;
  assignedToName: string | null;
  assignedToTeamCode: string | null;
}

export default function CoordinatorAppointments() {
  const [rows, setRows] = useState<AppointmentRow[]>([]);
  const [viewerTeamCode, setViewerTeamCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${BASE}/api/coordinator/appointments`, { credentials: "include" }).then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      ),
      fetch(`${BASE}/api/user/profile-summary`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null),
    ])
      .then(([d, me]) => {
        setRows(d as AppointmentRow[]);
        setViewerTeamCode((me as { teamCode?: string | null } | null)?.teamCode ?? null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateStatus = async (id: number, status: string) => {
    setUpdating(id);
    try {
      const res = await fetch(`${BASE}/api/coordinator/requests/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (res.ok) load();
    } finally {
      setUpdating(null);
    }
  };

  return (
    <CoordinatorLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-center gap-3 mb-2">
          <CalendarDays className="h-6 w-6 text-sky-600" />
          <h1 className="text-2xl font-bold text-slate-900">Appointments queue</h1>
        </div>
        <p className="text-slate-600 mb-6">
          Appointment requests and changes submitted by patients. Confirm, decline or
          mark resolved once handled.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {error}
          </p>
        )}
        {!loading && !error && rows.length === 0 && (
          <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl p-6 text-center">
            No appointment requests in the queue.
          </p>
        )}

        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r.id}
              className="bg-white p-4 rounded-2xl border border-slate-200"
              data-testid={`appointment-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-slate-900">
                      {r.patientName ?? "Unknown patient"}
                    </p>
                    <StatusBadge status={r.status} />
                  </div>
                  <p className="text-xs text-slate-500">
                    Preferred: {r.preferredDate ?? "—"} · Submitted{" "}
                    {new Date(r.createdAt).toLocaleString("en-GB")}
                  </p>
                  {r.assignedToUserId && (
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
                      <User className="h-3 w-3 shrink-0" />
                      <span>
                        Assigned to{" "}
                        <span className="text-slate-700">
                          {r.assignedToName ?? r.assignedToUserId}
                        </span>
                        {r.assignedToTeamCode ? ` (Team ${r.assignedToTeamCode})` : ""}
                      </span>
                      <SameTeamBadge
                        viewerTeamCode={viewerTeamCode}
                        otherTeamCode={r.assignedToTeamCode}
                      />
                    </div>
                  )}
                  {r.detail && (
                    <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">
                      {r.detail}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={updating === r.id}
                    onClick={() => updateStatus(r.id, "confirmed")}
                    data-testid={`button-confirm-${r.id}`}
                  >
                    Confirm
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={updating === r.id}
                    onClick={() => updateStatus(r.id, "declined")}
                    data-testid={`button-decline-${r.id}`}
                  >
                    Decline
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={updating === r.id}
                    onClick={() => updateStatus(r.id, "resolved")}
                    data-testid={`button-resolve-${r.id}`}
                  >
                    Resolve
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CoordinatorLayout>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  const s = status ?? "pending";
  const tones: Record<string, string> = {
    pending: "bg-amber-100 text-amber-800",
    assigned: "bg-sky-100 text-sky-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    declined: "bg-rose-100 text-rose-800",
    forwarded: "bg-violet-100 text-violet-800",
    resolved: "bg-slate-100 text-slate-700",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tones[s] ?? tones["pending"]}`}>
      {s}
    </span>
  );
}
